<template>
  <div class="product-gallery">
    <div class="product-gallery-image" :class="{ draggable: zoomed }" ref="productImage">
      <PanZoom
        v-if="media && media.type === 'IMAGE'"
        class="product-gallery-panzoom"
        @zoom="onZoom"
        @zoomout="zoomOut"
        :isZoomed="zoomed"
        :options="{
          minZoom: 1,
          maxZoom: screenWidth >= 970 ? 2 : 8,
          zoomDoubleClickSpeed: screenWidth >= 970 ? 2 : 8, // Zoom directly to 2x or 8x magnification
          initialX: 300,
          initialZoom: 1,
          bounds: true,
          boundsPadding: 1,
          autocenter: false,
        }"
        ref="panzoom"
        :key="`${media.url}-${activeMedia || 'loading'}`"
        :prevent-default="false"
      >
        <img 
          :src="media.url" 
          :alt="`${media.alt} - Fullscreen View ${currentImageIndex} of ${gallerySize}.`" 
        />
      </PanZoom>
      <div class="product-gallery-video" v-else-if="media && media.type === 'VIDEO'">
        <VideoPlayer
          :key="media.url"
          :auto-load="true"
          :active="true"
          :poster="media.thumbnailUrl"
          :hls="media.hlsUrl"
          :dash="media.dashUrl"
          :mp4="media.url"
        ></VideoPlayer>
      </div>
    </div>
    <div>
      <Snackbar class="product-snackbar" :message="snackbarMessage" :show="notification" @update:show="clearNotification" />
    </div>
    <div class="product-gallery-navigation" :class="{ 'product-gallery-navigation-hidden': zoomed }">
      <a class="product-gallery-back" @click="goBack">
        <span class="product-gallery-back-icon af af-chevron-left"></span>
        Back<template v-if="product"> to {{ product.collection ? product.collection.name : product.title }}</template>
      </a>
      <ProductThumbnails
        :product="product"
        :medias="medias"
        :active-media="activeMedia"
        @clickThumbnail="updateActiveMedia"
        :vertical="screenWidth >= 970"
        ref="productThumbnails"
      ></ProductThumbnails>
    </div>
    <div class="product-gallery-carousel" ref="productImageControls">
      <div class="product-gallery-navigation-controls" v-if="mounted && !zoomed && screenWidth >= 970">
        <button class="product-gallery-left button white filled icon" @click="previousImage" aria-label="Previous gallery image"><span class="af af-chevron-left"></span></button>
        <button class="product-gallery-right button white filled icon" @click="nextImage"><span class="af af-chevron-right" aria-label="Next gallery image"></span></button>
      </div>
      <div class="product-gallery-actions">
        <button v-if="media && media.type === 'IMAGE' && zoomed" @click="zoomOut" class="product-gallery-zoom button white filled" aria-label="Zoom Out">Zoom Out</button>
        <button v-else-if="media && media.type === 'IMAGE'" @click="zoomIn" class="product-gallery-zoom button white filled" aria-label="Zoom In">Zoom In</button>
        <button @click="goBack" class="product-gallery-close button white filled icon" aria-label="Exit Fullscreen"><span class="af af-close"></span></button>
      </div>
    </div>
  </div>
</template>
<script>
import { useRouter, useRoute } from 'vue-router';
import { ref, onBeforeMount, onMounted, onUnmounted, computed } from 'vue';
import { Swipe, Snackbar } from '@/pixel/index';
import PanZoom from '@/components/commons/PanZoom.vue';
import ProductThumbnails from '@/components/product/ProductThumbnails.vue';
import useResize from '@/compositions/useResize.js';
import useLifecycleHook from '@/compositions/useLifecycleHook';
import Tracking from '@/components/commons/Tracking';
import VideoPlayer from '@/components/product/Video.vue';

export default {
  name: 'ProductGallery',
  components: {
    PanZoom,
    ProductThumbnails,
    VideoPlayer,
    Snackbar,
  },
  props: {
    product: {
      default: null,
      type: Object,
    },
    activeMedia: {
      default: null,
      type: [Number, String],
    },
    notification: {
      type: Boolean,
    },
    medias: {
      default: null,
      type: Array,
    },
  },
  emits: ['updateActiveMedia', 'close', 'toggleSnackbar', 'update:notification', 'trackEvents'],

  setup(props, context) {
    const snackbarMessage = 'Double tap or pinch to zoom.';
    const router = useRouter();
    const route = useRoute();
    const { width } = useResize();
    const { mounted } = useLifecycleHook();
    const scale = ref(1);
    const zoomErrorDelta = ref(0.1);
    const productThumbnails = ref(null);
    const productImage = ref(null);
    const panzoom = ref(null);
    const zoomed = computed(() => {
      return scale.value > 1 + zoomErrorDelta.value;
    });
    const media = computed(() => {
      const index = route.query.gallery || props.activeMedia || route.query.bundleGallery || 0;
      if (props.product && props.product?.media) {
        return {
          ...props.product.media[index],
          alt: props.product.title
          };
      } else if (props?.medias) {
        return props?.medias[index] || {};
      }
      return {};
    });
    const gallerySize = computed(() => props.product?.media?.length || props.medias.length);
    const currentImageIndex = computed(() => {
      const imageIndex = props.activeMedia || 0;
      return imageIndex + 1;
    });
    let swipe = null;

    function updateActiveMedia(index) {
      productThumbnails.value?.scrollToThumbnail(index);
      if (props?.product && props?.product?.media) {
        router.replace({
          name: 'product',
          query: { gallery: index },
        });
      } else if (props?.medias) {
        if (window.location.href.includes('product')) {
          router.replace({
            name: 'product',
            query: { bundleGallery: index },
          });
        } else {
          router.replace({
            name: 'set',
            query: { bundleGallery: index },
          });
        }
      }
      context.emit('updateActiveMedia', index);
      context.emit('trackEvents', 'Gallery_View_Thumbnails_Clicked');
    }

    function previousImage() {
      if (!zoomed.value) {
        const index = props.activeMedia - 1;
        if (props?.product && props?.product?.media) {
          updateActiveMedia(index < 0 ? props.product.media.length - 1 : index);
        } else {
          updateActiveMedia(index < 0 ? props.medias.length - 1 : index);
        }
        context.emit('toggleSnackbar');
      }
    }

    function nextImage() {
      if (!zoomed.value) {
        const index = props.activeMedia + 1;
        if (props?.product && props?.product?.media) {
          updateActiveMedia(index > props.product.media.length - 1 ? 0 : index);
        } else {
          updateActiveMedia(index > props.medias.length - 1 ? 0 : index);
        }
        context.emit('toggleSnackbar');
      }
    }

    function zoomOut() {
      if (panzoom.value && panzoom.value.$el?.clientWidth && panzoom.value.$el?.clientHeight) {
        panzoom.value.smoothZoomAbs(panzoom.value.$el.clientWidth / 2, panzoom.value.$el.clientHeight / 2, 1 - zoomErrorDelta.value);
      }
    }

    function zoomIn() {
      if (panzoom.value && panzoom.value.$el?.clientWidth && panzoom.value.$el?.clientHeight) {
        panzoom.value.smoothZoomAbs(
          panzoom.value.$el.clientWidth / 2,
          panzoom.value.$el.clientHeight / 2,
          (width.value >= 970 ? 2 : 8) + zoomErrorDelta.value,
        );
        Tracking.track('PDP_Gallery_Click_Zoom', { device: window.screen.width < 967 ? 'Mobile' : 'Desktop' });
      }
    }

    function onZoom(e) {
      const transform = e.getTransform();
      scale.value = transform.scale;
    }

    function clearNotification() {
      context.emit('update:notification', false);
      return false;
    }

    function goBack() {
      context.emit('close');
    }

    function handleKeydown(e) {
      switch (e.key) {
        case 'ArrowLeft':
          previousImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case ' ':
        case '-': 
        case '_':
          zoomOut();
          break;     
        case 'ArrowUp':
          previousImage();
          break;
        case 'ArrowDown':
          nextImage();
          break;   
        case '+':
        case '=':
          zoomIn();
          break;
        case 'Escape':
          goBack();
          break;
      }
    }

    onBeforeMount(() => {
      window.addEventListener('keydown', handleKeydown);
    });

    onMounted(() => {
      swipe = new Swipe();
      swipe.setOnLeft(nextImage);
      swipe.setOnRight(previousImage);

      if (productImage.value) {
        swipe.attachListeners(productImage.value);
      }
      if (props.notification) {
        setTimeout(() => context.emit('update:notification', false), 6000);
      }
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown);

      if (swipe && productImage.value) {
        swipe.detachListeners(productImage.value);
      }
    });

    return {
      screenWidth: width,
      mounted,
      media,
      scale,
      productThumbnails,
      productImage,
      updateActiveMedia,
      zoomErrorDelta,
      panzoom,
      zoomOut,
      zoomIn,
      onZoom,
      previousImage,
      nextImage,
      zoomed,
      clearNotification,
      snackbarMessage,
      handleKeydown,
      goBack,
      gallerySize,
      currentImageIndex,
    };
  },
};
</script>

<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.product-gallery {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  right: 0;
  height: 100vh;
  height: -webkit-fill-available;
  background-color: var(--white);
  overflow: hidden;
  transition: opacity 0.3s ease;
  display: flex;
  flex-direction: column-reverse;
  -webkit-overflow-scrolling: touch;
  touch-action: none;

  @media (min-width: $tablet-breakpoint) {
    flex-direction: row;
  }
}

.product-gallery-image {
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;

  @media (min-width: $tablet-breakpoint) {
    padding-left: 230px;
  }

  &.draggable {
    cursor: grab;
  }
}

.product-gallery-panzoom {
}

.product-gallery-video {
  width: 100%;
  position: relative;
}

.product-gallery-navigation {
  flex-basis: 44px;
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  opacity: 1;
  transition: opacity 150ms ease-in-out;
  background-color: white;
  z-index: 100;

  &.product-gallery-navigation-hidden {
    opacity: 0;
    pointer-events: none;
  }

  @media (min-width: $tablet-breakpoint) {
    flex-basis: 230px;
    border-right: 1px solid var(--grey-ultra-light);
  }
}

.product-gallery-back {
  display: none;
  align-items: center;
  padding-top: var(--space-4);
  padding-bottom: var(--space-4);
  padding-left: var(--space-4);
  border-bottom: 1px solid var(--grey-ultra-light);

  @media (min-width: $tablet-breakpoint) {
    display: flex;
  }
}

.product-gallery-back-icon {
  margin-right: var(--space-2);
}

.product-thumbnails {
  overflow-y: scroll;
}

.product-thumbnail {
  display: block;
  margin-top: var(--space-4);
  margin-left: var(--space-4);
  margin-right: var(--space-4);
  border-bottom: 2px solid white;
  transition: border 150ms ease-in-out;
  height: 103px;

  /* purgecss ignore */
  &:hover,
  &.active {
    border-bottom: 2px solid var(--coral);
  }
}

.product-gallery-carousel {
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
  opacity: 1;
  transition: opacity 150ms ease-in-out;
  pointer-events: none;
}

.product-gallery-navigation-controls {
  position: relative;
}

.product-gallery-actions {
  position: absolute;
  top: var(--space-4);
  right: var(--space-4);
  display: flex;
}

.product-gallery-zoom,
.product-gallery-close {
  pointer-events: all;
}

.product-gallery-zoom {
  margin-right: var(--space-2);
}

.product-gallery-left,
.product-gallery-right {
  position: absolute;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  width: 40px;
  height: 40px;
  border: 1px solid transparent;
  border-radius: 50%;
  pointer-events: all;

  @media (min-width: $tablet-breakpoint) {
    border: 1px solid var(--grey-ultra-light);
  }
}

.product-gallery-left {
  left: var(--space-4);
}

.product-gallery-right {
  right: var(--space-4);
}

.product-carousel {
  scroll-snap-type: x mandatory;
  overflow-x: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.product-carousel-slide {
  display: inline-block;
  scroll-snap-align: start;
  flex-basis: 100%;
  flex-grow: 0;
  flex-shrink: 0;
}

.product-gallery-transition-enter-from,
.product-gallery-transition-leave-to {
  opacity: 0;
}

.product-gallery-transition-enter-active,
.product-gallery-transition-leave-active {
  transition: opacity 350ms ease-in-out;
}

.product-snackbar {
  margin-bottom: var(--space-10);

  @media (max-width: 969px) and (min-width: 768px) {
    left: calc(50% - 172px);
  }
  @media (min-width: $tablet-breakpoint) and (max-width: 1900px) {
    left: calc(50% - 172px);
    margin-bottom: var(--space-0);
  }
}
</style>
